import { useCallback } from 'react';
import { type Edge, type Node, type ReactFlowInstance } from '@xyflow/react';
import * as Api from '@ViewModels';
import { toast } from 'sonner';
import type { BaseNodeData, FlowContact } from '../types';
import { NODE_TYPES } from '../types';
import { deriveParentCaseInfoForNewNode } from '../utils/branchParentCase';
import type { ChainTerminus } from './useChainTermini';
import type { HistoryActionType } from './useUndoRedo';
import { addSwitchStepWithSegments } from './useCanvasDragDrop';
import { insertNodeAfter } from '../utils/autoLayout';
import { createStepNodeByType } from '../utils/nodeFactory';
import {
	buildPersistedSwitchCaseAssignments,
	syncOptimisticSwitchEdges,
	syncOptimisticSwitchNodes,
} from '../utils/optimisticSwitchSync';
import { reconcileOptimisticStepNode } from '../utils/reconcileOptimisticStepNode';
import { buildTemplateForNode, resolveTargetTemplateIdForTerminus } from '../utils/templateResolution';
import { scheduleCenterOnNode } from '../utils/viewport';
import {
	canUseTriggerAutomationStep,
	isTriggerAutomationStepNode,
	TRIGGER_AUTOMATION_LAST_STEP_TOAST,
	TRIGGER_AUTOMATION_TRIGGER_TYPE_TOAST,
} from '../utils/triggerAutomationStepRules';

interface UseChainStepAdditionParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	animateEdges: boolean;
	activeContacts: FlowContact[];
	setCenter: ReactFlowInstance['setCenter'];
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	onAddStepToChain?: (
		terminus: ChainTerminus | null,
		stepType: string
	) => Promise<Api.IAutomationStep | null | undefined>;
	/** The automation template - needed for creating new step nodes with proper context */
	automationTemplate?: Api.IAutomationTemplate;
	/** Step node types that are unavailable in the current editor context */
	excludedStepTypes?: string[];
}

type HandleAddStepToChain = (terminus: ChainTerminus, stepType: string) => Promise<void>;

/**
 * Hook that handles adding steps to chain termini (floating + buttons).
 * Extracted from AutomationFlowCanvas to improve maintainability.
 */
export function useChainStepAddition({
	nodes,
	edges,
	setNodes,
	setEdges,
	animateEdges,
	activeContacts,
	setCenter,
	takeSnapshot,
	onAddStepToChain,
	automationTemplate,
	excludedStepTypes,
}: UseChainStepAdditionParams): HandleAddStepToChain {
	const handleAddStepToChain = useCallback(
		async (terminus: ChainTerminus, stepType: string) => {
			try {
				if (isTriggerAutomationStepNode(terminus.node)) {
					toast.error(TRIGGER_AUTOMATION_LAST_STEP_TOAST);
					return;
				}

				if (excludedStepTypes?.includes(stepType)) {
					toast.error('This step type is not available for this automation.');
					return;
				}

				if (
					stepType === NODE_TYPES.TRIGGER_AUTOMATION_STEP &&
					!canUseTriggerAutomationStep(automationTemplate?.draft?.trigger ?? automationTemplate?.published?.trigger)
				) {
					toast.error(TRIGGER_AUTOMATION_TRIGGER_TYPE_TOAST);
					return;
				}

				const targetTemplateId = resolveTargetTemplateIdForTerminus({
					terminus,
					nodes,
					rootTemplateId: automationTemplate?.id,
				});
				const existingTemplate = (terminus.node.data as { automationTemplate?: Api.IAutomationTemplate })
					.automationTemplate;
				const nodeTemplate = buildTemplateForNode({
					rootTemplate: automationTemplate,
					targetTemplateId,
					existingTemplate,
				});

				// Handle switch step specially - creates segments
				if (stepType === NODE_TYPES.SWITCH_STEP) {
					takeSnapshot('node_add', `Add ${stepType} step`);

					const parentNode = nodes.find(node => node.id === terminus.nodeId);
					const basePosition = parentNode?.position ?? { x: 0, y: 0 };

					const switchResult = addSwitchStepWithSegments(nodes, edges, basePosition, terminus.nodeId, nodeTemplate);

					const switchEdges = switchResult.edges.map(edge => ({
						...edge,
						animated: animateEdges,
						data: { ...edge.data, activeContacts },
					}));

					setNodes(switchResult.nodes);
					setEdges(switchEdges);

					const focusNode = switchResult.nodes.find(node => node.id === switchResult.focusNodeId);
					if (focusNode) {
						scheduleCenterOnNode(setCenter, focusNode, { duration: 300, zoom: 1 });
					}

					if (onAddStepToChain) {
						const createdStep = await onAddStepToChain(terminus, stepType);
						if (createdStep?.id) {
							const persistedSwitchStep = createdStep as Api.ISwitchAutomationStep;
							const caseAssignments = buildPersistedSwitchCaseAssignments({
								nodes: switchResult.nodes,
								optimisticSwitchNodeId: switchResult.switchNodeId,
								persistedSwitchStep,
							});
							const realSwitchNodeId = `step-${createdStep.id}`;

							setNodes(currentNodes =>
								syncOptimisticSwitchNodes({
									nodes: currentNodes,
									optimisticSwitchNodeId: switchResult.switchNodeId,
									persistedSwitchStep,
									automationTemplate: nodeTemplate,
									caseAssignments,
								})
							);
							setEdges(currentEdges =>
								syncOptimisticSwitchEdges({
									edges: currentEdges,
									optimisticSwitchNodeId: switchResult.switchNodeId,
									realSwitchNodeId,
									caseAssignments,
								})
							);
						}
					}
					return;
				}

				// Create optimistic node for other step types
				const newNode = createStepNodeByType(stepType, { x: 0, y: 0 }, undefined, nodeTemplate);
				if (!newNode) {
					toast.error(`Unknown step type: ${stepType}`);
					return;
				}

				// Enrich node with parentCaseInfo so branch metadata and behavior scheduling
				// copy remain stable even before a full flow refresh.
				const parentCaseInfo = deriveParentCaseInfoForNewNode(terminus, nodes);
				if (parentCaseInfo) {
					newNode.data = {
						...newNode.data,
						parentCaseInfo,
					};
				}

				takeSnapshot('node_add', `Add ${stepType} step`);

				// Insert node after the terminus with auto-layout.
				// All terminus nodes (segments, step nodes) use a source handle with
				// id='default'. The 'case-X' handles only exist on switch step nodes,
				// which are never termini. Pass undefined to use the default handle.
				const result = insertNodeAfter(nodes, edges, terminus.nodeId, newNode, undefined);

				const updatedEdges = result.edges.map(edge => ({
					...edge,
					animated: animateEdges,
					data: { ...edge.data, activeContacts },
				}));

				setNodes(result.nodes);
				setEdges(updatedEdges);

				// Focus on newly added node
				const addedNode = result.nodes.find(n => n.id === newNode.id);
				if (addedNode) {
					scheduleCenterOnNode(setCenter, addedNode, { duration: 300, zoom: 1 });
				}

				// Persist to backend if handler provided
				if (onAddStepToChain) {
					const createdStep = await onAddStepToChain(terminus, stepType);
					if (createdStep?.id) {
						reconcileOptimisticStepNode({
							optimisticNodeId: newNode.id,
							createdStep,
							automationTemplate: nodeTemplate,
							setNodes,
							setEdges,
						});
					}
				}
			} catch {
				toast.error('Failed to add step. Please try again.');
			}
		},
		[
			onAddStepToChain,
			nodes,
			edges,
			animateEdges,
			activeContacts,
			setNodes,
			setEdges,
			setCenter,
			takeSnapshot,
			automationTemplate,
			excludedStepTypes,
		]
	);

	return handleAddStepToChain;
}
